import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Card, CardContent, CircularProgress,
  Grid, LinearProgress, Paper, Snackbar, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
  useTheme, useMediaQuery, Stack
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { academicAPI, assessmentAPI, teacherAPI } from '../../services/api';
import ModulePageHeader from '../common/ModulePageHeader';
import { EmptyRecordsState } from '../common/ResponsiveRecordList';
import TablePagination from '../common/TablePagination';

const extractCollection = (r) => {
  const p = r?.data !== undefined ? r.data : r;
  if (Array.isArray(p)) return p;
  if (Array.isArray(p?.data)) return p.data;
  if (Array.isArray(p?.data?.data)) return p.data.data;
  return [];
};
const extractPayload = (r) => (r?.data !== undefined ? r.data : r);
const getSubjectName = (s) => s?.subject_name || s?.name || s?.title || '-';
const normalizePercentage = (v) => { const n = Number(v); return Number.isNaN(n) ? 0 : Math.min(100, Math.max(0, n)); };
const paginate = (arr, page, perPage) => arr.slice((page - 1) * perPage, page * perPage);
const buildPagination = (arr, page, perPage) => ({ current_page: page, last_page: Math.ceil(arr.length / perPage), per_page: perPage, total: arr.length });
const teacherLabel = (t) => t?.full_name || `${t?.last_name || ''} ${t?.first_name || ''}`.trim() || `Teacher ${t?.id}`;

const sx = { width: '100%', minWidth: { sm: 240, lg: 280 } };

const TeacherWorkload = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [classes, setClasses] = useState([]);
  const [terms, setTerms] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('');
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [academicYear, setAcademicYear] = useState(new Date().getFullYear());
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [rows, setRows] = useState(null);
  const [loadingWorkload, setLoadingWorkload] = useState(false);
  const [selectedAssessmentType, setSelectedAssessmentType] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchFilters = useCallback(async () => {
    setLoadingFilters(true);
    try {
      const [cr, tr, thr] = await Promise.allSettled([
        academicAPI.getClasses(), academicAPI.getTerms(), teacherAPI.getActive()
      ]);
      setClasses(cr.status === 'fulfilled' ? extractCollection(cr.value) : []);
      const fetchedTerms = tr.status === 'fulfilled' ? extractCollection(tr.value) : [];
      setTerms(fetchedTerms);
      setTeachers(thr.status === 'fulfilled' ? extractCollection(thr.value) : []);
      const current = fetchedTerms.find((t) => t.is_current || t.current);
      if (current) { setSelectedTerm(current.id); setAcademicYear(current.year || current.academic_year || new Date().getFullYear()); }
    } catch {
      setSnackbar({ open: true, message: 'Failed to load filters', severity: 'error' });
    } finally {
      setLoadingFilters(false);
    }
  }, []);

  const fetchWorkload = useCallback(async () => {
    if (!selectedTeacher || !selectedTerm) { setRows(null); return; }
    setLoadingWorkload(true);
    try {
      const params = { academic_year: academicYear, term_id: selectedTerm, ...(selectedClass && { class_id: selectedClass }) };
      const payload = extractPayload(await assessmentAPI.getTeacherWorkload(selectedTeacher, params));
      const data = payload?.data || payload;
      setRows(Array.isArray(data?.assessments) ? data.assessments : extractCollection(data));
    } catch {
      setRows([]);
    } finally {
      setLoadingWorkload(false);
    }
  }, [academicYear, selectedClass, selectedTeacher, selectedTerm]);

  useEffect(() => { fetchFilters(); }, [fetchFilters]);

  useEffect(() => { setRows(null); setPage(1); setSelectedAssessmentType(''); fetchWorkload(); }, [fetchWorkload]);

  const assessmentTypes = rows ? [...new Map(rows.filter((r) => r?.assessment_type).map((r) => [r.assessment_type.id || r.assessment_type.assessment_name, r.assessment_type])).values()] : [];
  const filteredRows = rows ? (selectedAssessmentType ? rows.filter((r) => String(r?.assessment_type?.id || r?.assessment_type?.assessment_name) === String(selectedAssessmentType)) : rows) : [];

  const pagedRows = paginate(filteredRows, page, perPage);
  const pagination = buildPagination(filteredRows, page, perPage);

  const WorkloadRow = ({ row, index }) => {
    const subjectName = getSubjectName(row?.class_stream_subject_paper?.subject_paper?.subject);
    const assessmentType = row?.assessment_type?.assessment_name || row?.assessment_type?.name || '-';
    const classLabel = row?.class_stream_subject_paper?.class?.name || row?.class_stream_subject_paper?.class?.class_name || '-';
    const marked = Number(row?.students_marked ?? 0);
    const pending = Number(row?.students_pending ?? 0);
    const total = marked + pending;
    const completion = total > 0 ? normalizePercentage((marked / total) * 100) : 0;

    if (isMobile) {
      return (
        <Stack spacing={1} sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography fontWeight={700}>{subjectName}</Typography>
          <Typography variant="body2" color="text.secondary">{classLabel} · {assessmentType}</Typography>
          <Typography variant="body2" color="text.secondary">Marked: {marked} / {total}</Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <LinearProgress variant="determinate" value={completion} sx={{ flexGrow: 1, height: 8, borderRadius: 1 }} />
            <Typography variant="body2">{completion}%</Typography>
          </Box>
        </Stack>
      );
    }
    return (
      <TableRow key={row?.id || index}>
        <TableCell>{subjectName}</TableCell>
        <TableCell>{classLabel}</TableCell>
        <TableCell>{assessmentType}</TableCell>
        <TableCell>{marked}</TableCell>
        <TableCell>{pending}</TableCell>
        <TableCell>
          <Box display="flex" alignItems="center" gap={1}>
            <LinearProgress variant="determinate" value={completion} sx={{ width: 80, height: 8, borderRadius: 1 }} />
            <Typography variant="body2">{completion}%</Typography>
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Box>
      <ModulePageHeader
        title="Teacher Workload"
        subtitle="View assessment workload per teacher"
        actions={[
          <Button key="refresh" variant="outlined" startIcon={<RefreshIcon />}
            onClick={() => { setRows(null); fetchFilters(); }}
            disabled={loadingFilters || loadingWorkload} fullWidth={isMobile}
          >Refresh</Button>
        ]}
      />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={2}>
              <TextField fullWidth size="small" type="number" label="Academic Year"
                value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}
                inputProps={{ min: 2000, max: 2100 }} disabled={loadingFilters}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Autocomplete fullWidth sx={sx} options={terms}
                value={terms.find((t) => String(t.id) === String(selectedTerm)) || null}
                onChange={(_, v) => setSelectedTerm(v?.id || '')}
                getOptionLabel={(o) => `${o.name || 'Term'} ${o.year || ''}`.trim()}
                isOptionEqualToValue={(o, v) => o.id === v.id} disabled={loadingFilters}
                renderInput={(p) => <TextField {...p} label="Term" size="small" />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Autocomplete fullWidth sx={sx} options={classes}
                value={classes.find((c) => String(c.id) === String(selectedClass)) || null}
                onChange={(_, v) => setSelectedClass(v?.id || '')}
                getOptionLabel={(o) => o.name || o.class_name || 'Class'}
                isOptionEqualToValue={(o, v) => o.id === v.id} disabled={loadingFilters}
                renderInput={(p) => <TextField {...p} label="Class (Optional)" size="small" />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Autocomplete fullWidth sx={sx} options={teachers}
                value={teachers.find((t) => String(t.id) === String(selectedTeacher)) || null}
                onChange={(_, v) => setSelectedTeacher(v?.id || '')}
                getOptionLabel={teacherLabel}
                isOptionEqualToValue={(o, v) => o.id === v.id} disabled={loadingFilters}
                renderInput={(p) => <TextField {...p} label="Teacher" size="small" />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Autocomplete fullWidth sx={sx} options={assessmentTypes}
                value={assessmentTypes.find((t) => String(t.id || t.assessment_name) === String(selectedAssessmentType)) || null}
                onChange={(_, v) => { setSelectedAssessmentType(v ? String(v.id || v.assessment_name) : ''); setPage(1); }}
                getOptionLabel={(o) => o.assessment_name || o.name || '-'}
                isOptionEqualToValue={(o, v) => (o.id || o.assessment_name) === (v.id || v.assessment_name)}
                disabled={!rows}
                renderInput={(p) => <TextField {...p} label="Assessment Type" size="small" />}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {!selectedTerm || !selectedTeacher ? (
        <Card>
          <CardContent>
            <Typography color="text.secondary">Select a term and teacher to view workload.</Typography>
          </CardContent>
        </Card>
      ) : loadingWorkload ? (
        <Box py={5} textAlign="center"><CircularProgress size={28} /></Box>
      ) : rows === null || rows.length === 0 ? (
        <Card>
          <CardContent><EmptyRecordsState message="No workload data found for this teacher." /></CardContent>
        </Card>
      ) : isMobile ? (
        <Card>
          <CardContent>
            <Box>{pagedRows.map((row, i) => <WorkloadRow key={row?.id || i} row={row} index={i} />)}</Box>
            <TablePagination pagination={pagination} onPageChange={setPage} onPageSizeChange={(s) => { setPerPage(s); setPage(1); }} isMobile />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent>
            <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 760 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Subject</TableCell>
                    <TableCell>Class</TableCell>
                    <TableCell>Assessment Type</TableCell>
                    <TableCell>Marked</TableCell>
                    <TableCell>Pending</TableCell>
                    <TableCell>Completion</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedRows.map((row, i) => <WorkloadRow key={row?.id || i} row={row} index={i} />)}
                </TableBody>
              </Table>
              <TablePagination pagination={pagination} onPageChange={setPage} onPageSizeChange={(s) => { setPerPage(s); setPage(1); }} isMobile={isMobile} />
            </TableContainer>
          </CardContent>
        </Card>
      )}

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TeacherWorkload;
